import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    id: "8I-NNGZZD6o",
    name: "Dr. Barry Lejeune",
    headline: "From $1.2M to $2.3M — Found the Growth Hiding in Plain Sight",
  },
  {
    id: "j6VQpsXfq68",
    name: "Dr. Richardson",
    headline: "Found the Leaks & Filled the Schedule",
  },
  {
    id: "m_mRvN4hG7g",
    name: "Dr. Garland Davis",
    headline: "A Growth Partner for Over a Decade",
  },
  {
    id: "EP_sCX1JyxA",
    name: "Dr. Patel",
    headline: "Broke Through the Plateau with a Clear Plan",
  },
  {
    id: "438tvAtvg6E",
    name: "Dr. Ron Murphy",
    headline: "Finally Saw Where the Growth Was Hiding",
  },
];

const TestimonialCarousel = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const prev = () => setActive((a) => (a === 0 ? testimonials.length - 1 : a - 1));
  const next = () => setActive((a) => (a === testimonials.length - 1 ? 0 : a + 1));

  const current = testimonials[active];

  return (
    <section className="py-16 md:py-20 bg-background" ref={ref}>
      <div className="max-w-3xl mx-auto section-padding">
        <div className={`text-center mb-10 ${visible ? "reveal-up" : "opacity-0"}`}>
          <p className="text-accent font-semibold text-sm tracking-wide uppercase mb-3">
            Don't Just Take Our Word for It
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight mb-3">
            Hear It Straight from Our Clients
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Real dentists. Real results. See why practices trust us to drive their growth year after year.
          </p>
        </div>

        <div className={`${visible ? "reveal-up" : "opacity-0"}`} style={{ animationDelay: "150ms" }}>
          {/* Video */}
          <div className="aspect-video rounded-xl overflow-hidden shadow-xl ring-1 ring-border mb-6">
            <iframe
              key={current.id}
              src={`https://www.youtube-nocookie.com/embed/${current.id}?rel=0`}
              title={`${current.name} testimonial`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              loading="lazy"
            />
          </div>

          {/* Caption + nav */}
          <div className="flex items-center justify-between">
            <button
              onClick={prev}
              className="p-2 rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="text-center">
              <p className="font-semibold text-foreground">{current.name}</p>
              <p className="text-muted-foreground text-sm">{current.headline}</p>
            </div>

            <button
              onClick={next}
              className="p-2 rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  i === active ? "bg-accent" : "bg-muted-foreground/30"
                }`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
